import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Shield,
  Car,
  Home,
  CreditCard,
  LogOut,
  Building2,
  ChevronRight,
  MessageSquare
} from 'lucide-react';
import { ChecklistType, CHECKLIST_NAMES } from '../types/datamodel';
import { User, Bank } from '../types/datamodel';

interface SidebarProps {
  user: User;
  currentBank: Bank | null;
  viewAllBanks: boolean;
  onLogout: () => void;
}

const checklistIcons: Record<ChecklistType, React.FC<any>> = {
  ibip: FileText,
  protection: Shield,
  motor: Car,
  pnc: Home,
  ppi: CreditCard
};

export function Sidebar({ user, currentBank, viewAllBanks, onLogout }: SidebarProps) {
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const checklistTypes: ChecklistType[] = ['ibip', 'protection', 'motor', 'pnc', 'ppi'];

  return (
    <div className="w-64 bg-gray-900 text-white h-screen flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="w-8 h-8 text-indigo-400" />
          <div>
            <h1 className="font-bold text-lg">Checklist</h1>
            <p className="text-xs text-gray-400">Banche Terze</p>
          </div>
        </div>

        {/* User Info */}
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold">{user.username.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{user.username}</p>
              <p className="text-xs text-gray-400">
                {user.role === 'company' ? 'Compagnia' : 'Banca'}
              </p>
            </div>
          </div>

          {(currentBank || viewAllBanks) && (
            <div className="flex items-center space-x-2 text-xs text-gray-300 bg-gray-700 rounded px-2 py-1">
              <Building2 className="w-3 h-3" />
              <span className="truncate">
                {viewAllBanks ? 'Tutte le Banche' : currentBank?.name}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {/* Dashboard */}
        <Link
          to="/"
          className={`flex items-center space-x-3 px-6 py-3 transition-colors ${
            location.pathname === '/'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
          }`}
        >
          <LayoutDashboard className="w-5 h-5" />
          <span className="font-medium">Dashboard</span>
        </Link>

        {/* Assistente Checklist */}
        <Link
          to="/agent"
          className={`flex items-center space-x-3 px-6 py-3 transition-colors ${
            location.pathname === '/agent'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
          }`}
        >
          <MessageSquare className="w-5 h-5" />
          <span className="font-medium">Assistente Checklist</span>
        </Link>

        {/* Divider */}
        <div className="px-6 py-3">
          <div className="border-t border-gray-700"></div>
          <p className="text-xs text-gray-500 mt-3 mb-2 uppercase tracking-wider">Checklist</p>
        </div>

        {/* Checklist Links */}
        {checklistTypes.map(type => {
          const Icon = checklistIcons[type];
          const path = `/checklist/${type}`;
          const active = isActive(path);

          return (
            <Link
              key={type}
              to={path}
              className={`flex items-center justify-between px-6 py-3 transition-colors group ${
                active
                  ? 'bg-gray-800 text-white border-l-4 border-indigo-500'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{CHECKLIST_NAMES[type]}</span>
              </div>
              <ChevronRight className={`w-4 h-4 transition-transform ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`} />
            </Link>
          );
        })}

        {/* Company Dashboard (solo per utenti Compagnia) */}
        {user.role === 'company' && (
          <>
            <div className="px-6 py-3 mt-4">
              <div className="border-t border-gray-700"></div>
              <p className="text-xs text-gray-500 mt-3 mb-2 uppercase tracking-wider">Compagnia</p>
            </div>

            <Link
              to="/company-overview"
              className={`flex items-center space-x-3 px-6 py-3 transition-colors ${
                location.pathname === '/company-overview'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <Building2 className="w-5 h-5" />
              <span className="font-medium">Vista Tutte le Banche</span>
            </Link>
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-700">
        <button
          onClick={onLogout}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
}
